import {
	type Currencies,
	type InvestmentBenchmark,
	type InvestmentReference,
	EditorSaveEditOrReviewRequestPortfolioSavingModeEnum,
	EntityEditorControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import type { InstrumentEntities } from "$root/functional-areas/instruments-editor/const";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { createPrimaryBenchmarkLabel, unifyReferenceAndCustomBenchmark } from "$root/functional-areas/reference/common";
import { useUserValue } from "$root/functional-areas/user";
import { userRepository } from "$root/functional-areas/user/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { minutes, valueByPath } from "$root/utils";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type SelectOption,
	type SelectOptions,
	Button,
	FormField,
	Label,
	SubmitButton,
	Radio,
	RadioGroup,
} from "@mdotm/shadcn/mdotm/components";
import { CircularProgressBar, Dialog, DialogFooter, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import {
	type SpawnResult,
	adaptAnimatedNodeProvider,
	spawn,
	useRefProxyWithState,
} from "@mdotm/shadcn/legacy/react-extensions";
import { useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { canSendToExecution } from "$root/functional-areas/portfolio/execution";

export type ManualCreationFormProps = {
	baseCurrency?: Currencies;
	name?: string;
	primaryBenchmark?: InvestmentBenchmark;
	investmentReference?: InvestmentReference;
	portfolioSavingMode?: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum;
	fixedWeights: boolean;
	liveTrading?: boolean;
};

function groupSelectOptions<T>(
	rows: Array<SelectOption<T> & { group?: { id: string; label?: string } | string }>,
): SelectOptions<T> {
	const byGroup = new Map<string, SelectOption<T>[]>();
	for (const { group, ...opt } of rows) {
		const key = typeof group === "string" ? group : (group?.label ?? group?.id ?? "");
		const arr = byGroup.get(key) ?? [];
		arr.push(opt as SelectOption<T>);
		byGroup.set(key, arr);
	}
	return [...byGroup.entries()];
}

type SubmitManualCreationDialogMode = "save-as-portfolio" | "save-as-draft";
const defaultSubmitManualCreationDialogMode: SubmitManualCreationDialogMode = "save-as-portfolio";
export type SubmitManualCreationPayload = ManualCreationFormProps & { submitMode: SubmitManualCreationDialogMode };
export type SubmitManualCreationDialogProps = {
	includesInvestments: boolean;
	defaultManualCreation?: ManualCreationFormProps;
	onAsyncSubmit(payload: SubmitManualCreationPayload): MaybePromise<void>;
	entity: Extract<InstrumentEntities, "INVESTMENT" | "INVESTMENT_DRAFT">;
	/**
	 * Set when the composition is in quantities: its AUM is held in `currency`, which the base currency
	 * is locked to, and a portfolio in quantities drifts, so the weighting method is locked to Drifting.
	 */
	quantities?: { currency: Currencies };
} & MinimumDialogProps;

function SubmitManualCreationDialog(props: SubmitManualCreationDialogProps) {
	const {
		includesInvestments,
		show,
		onClose,
		onAnimationStateChange,
		defaultManualCreation = { fixedWeights: false, liveTrading: false },
		onAsyncSubmit,
		entity,
		quantities,
	} = props;

	const { t } = useTranslation();
	const entityEditorApi = useApiGen(EntityEditorControllerApiFactory);
	const user = useUserValue();
	const customerQuery = userRepository.customer.useQuery(user.customerId!);

	const submitModeRef = useRef<SubmitManualCreationDialogMode>(defaultSubmitManualCreationDialogMode);
	const [submitMode, setSubmitMode] = useRefProxyWithState(defaultSubmitManualCreationDialogMode, submitModeRef);

	const { checkIfNameIsAvailable, checkingNameUniqueness } = useDebouncedNameUniquenessChecker({
		isNameAvailableApi: portfolioRepository.isInvestmentNameAvailable,
		currentName: null,
	});

	const { control, formState, handleSubmit } = useForm({
		defaultValues: quantities
			? { ...defaultManualCreation, baseCurrency: quantities.currency, fixedWeights: false }
			: defaultManualCreation,
		resolver: zodResolver(
			z.object(
				(submitMode === "save-as-draft"
					? {
							name: z
								.string()
								.min(1, t("MISSING_FIELD.INVESTMENT"))
								.refine(
									(name) => {
										if (defaultManualCreation.name || entity === "INVESTMENT_DRAFT") {
											return true;
										}
										return checkIfNameIsAvailable(name);
									},
									{
										message: t("MISSING_FIELD.NAME_NOT_AVAILABLE"),
									},
								),
							baseCurrency: z.string().optional(),
							primaryBenchmark: z
								.object(
									{
										benchmarkIdentifier: z.string(),
										benchmarkType: z.string(),
									},
									{
										invalid_type_error: "Please select a benchmark",
										required_error: "Please select a valid benchmark",
									},
								)
								.optional()
								.nullable(),
							investmentReference: z
								.object({
									referenceType: z.any(),
									referenceIdentifier: z.string().nullable(),
								} satisfies Record<keyof InvestmentReference, unknown>)
								.optional()
								.nullable(),
							portfolioSavingMode: z.nativeEnum(EditorSaveEditOrReviewRequestPortfolioSavingModeEnum).optional(),
							fixedWeights: z.boolean(),
							liveTrading: z.boolean().optional(),
						}
					: {
							name: z
								.string()
								.min(1, t("MISSING_FIELD.INVESTMENT"))
								.refine(
									(name) => {
										if (defaultManualCreation.name || entity === "INVESTMENT_DRAFT") {
											return true;
										}
										return checkIfNameIsAvailable(name);
									},
									{
										message: t("MISSING_FIELD.NAME_NOT_AVAILABLE"),
									},
								),
							primaryBenchmark: z
								.object(
									{
										benchmarkIdentifier: z.string(),
										benchmarkType: z.string(),
									},
									{
										invalid_type_error: "Please select a benchmark",
										required_error: "Please select a valid benchmark",
									},
								)
								.required()
								.passthrough(),
							baseCurrency: z.string(),
							investmentReference: z
								.object({
									referenceType: z.any(),
									referenceIdentifier: z.string().nullable(),
								} satisfies Record<keyof InvestmentReference, unknown>)
								.optional()
								.nullable(),
							portfolioSavingMode: includesInvestments
								? z.nativeEnum(EditorSaveEditOrReviewRequestPortfolioSavingModeEnum)
								: z.nativeEnum(EditorSaveEditOrReviewRequestPortfolioSavingModeEnum).optional(),
							fixedWeights: z.boolean(),
							liveTrading: z.boolean().optional(),
						}) satisfies Record<keyof ManualCreationFormProps, unknown>,
			),
		),
	});

	const selectableQuery = useQueryNoRefetch({
		queryKey: ["selectable", "currencies", "benchmarks"],
		queryFn: async () => {
			const selectable = await axiosExtract(entityEditorApi.getEditorNewSelectableMainInfo());

			const currencies = (selectable.availableCurrencies ?? []).map(
				(currency): SelectOption<Currencies> => ({
					label: currency ?? "",
					value: currency,
				}),
			);

			const benchmarks = groupSelectOptions<InvestmentBenchmark>(
				(selectable.availablePrimaryBenchmarks ?? []).map((el) => ({
					label: createPrimaryBenchmarkLabel(el),
					value: { benchmarkIdentifier: el.benchmarkIdentifier, benchmarkType: el.benchmarkType },
					group: t(
						`INVESTMENT_REFERENCE_CATEGORIES.${unifyReferenceAndCustomBenchmark(
							el.benchmarkType!,
							el.flaggedAsBenchmark,
						)}`,
					),
					disabled: !el.available,
				})),
			);

			const references = groupSelectOptions<InvestmentReference>(
				(selectable.availableInvestmentReferences ?? []).map((el) => ({
					label: el.name ?? "-",
					value: { referenceIdentifier: el.identifier, referenceType: el.type } satisfies InvestmentReference,
					group: t(`INVESTMENT_REFERENCE_CATEGORIES.${el.type!}`),
					disabled: !el.available,
				})),
			);

			return { currencies, benchmarks, references };
		},
		cacheTime: minutes(5),
		staleTime: 0,
	});

	const { data: customer } = customerQuery;
	const executionAvailable = useMemo(() => canSendToExecution(customer), [customer]);

	return (
		<Dialog
			size="medium"
			noValidate
			show={show}
			onClose={onClose}
			header={entity === "INVESTMENT" ? "Save portfolio" : "Save portfolio draft"}
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
					secondaryAction={
						<SubmitButton
							palette="secondary"
							data-qualifier="CompositionEditor/Modal/SaveAsDraft"
							onClick={() => flushSync(() => setSubmitMode("save-as-draft"))}
						>
							{entity === "INVESTMENT_DRAFT" ? t("BUTTON.SAVE_DRAFT") : t("BUTTON.SAVE_AS_DRAFT")}
						</SubmitButton>
					}
					primaryAction={
						<SubmitButton
							data-qualifier="CompositionEditor/Modal/Save"
							onClick={() => flushSync(() => setSubmitMode("save-as-portfolio"))}
						>
							{t("BUTTON.GENERATE")}
						</SubmitButton>
					}
				/>
			}
			onSubmitAsync={async () => {
				await handleSubmit((payload) => onAsyncSubmit({ ...payload, submitMode }))();
			}}
		>
			<div className="grid gap-4 transition-all ease-elastic">
				<FormFields.Text
					control={control}
					formState={formState}
					name="name"
					label="Portfolio name"
					data-qualifier="CompositionEditor/SaveDialog/Name"
					placeholder="Name"
					disabled={entity === "INVESTMENT_DRAFT"}
					rightContent={
						checkingNameUniqueness ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
					}
				/>

				<ReactQueryWrapperBase
					query={selectableQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{({ currencies }) => (
						<FormFields.Select
							control={control}
							formState={formState}
							name="baseCurrency"
							label="Base currency"
							placeholder={t("PORTFOLIOS.PORTFOLIO_CURRENCY_PLACEHOLDER")}
							style={{ width: "100%" }}
							options={currencies}
							disabled={quantities != null}
							data-qualifier="CompositionEditor/SaveDialog/Currency"
							enableSearch
						/>
					)}
				</ReactQueryWrapperBase>
				<ReactQueryWrapperBase
					query={selectableQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{({ benchmarks }) => (
						<FormFields.Select
							enableSearch
							control={control}
							formState={formState}
							name="primaryBenchmark"
							options={benchmarks}
							placeholder={t("PORTFOLIOS.PORTFOLIO_BENCHMARK_PLACEHOLDER")}
							data-qualifier="CompositionEditor/SaveDialog/Benchmark"
							style={{ width: "100%" }}
							label={({ htmlFor }) => (
								<div className="flex items-center gap-2 mb-px">
									<Label htmlFor={htmlFor}>Benchmark</Label>
									<InfoTooltip>
										<TooltipContent>
											<Text type="Body/M/Book" as="div">
												Some benchmarks might be unavailable for selection if:
												<ul className="list-disc pl-8">
													<li>
														they are not in <strong>READY</strong> status
													</li>
													<li>
														they have <strong>drifting weights</strong> and the <strong>inception date</strong> is after
														the portfolio&apos;s inception
													</li>
													<li>
														they have <strong>fixed weights</strong>, but one or more instruments{" "}
														<strong>lack historical</strong> data covering the full portfolio timespan
													</li>
												</ul>
											</Text>
										</TooltipContent>
									</InfoTooltip>
								</div>
							)}
						/>
					)}
				</ReactQueryWrapperBase>

				<ReactQueryWrapperBase
					query={selectableQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{({ references }) => (
						<FormFields.Select
							enableSearch
							control={control}
							formState={formState}
							name="investmentReference"
							label="Reference"
							placeholder={t("PORTFOLIOS.PORTFOLIO_REFERENCE_PLACEHOLDER")}
							options={references}
							data-qualifier="CompositionEditor/SaveDialog/investmentReference"
							style={{ width: "100%" }}
							unselectOnMatch
						/>
					)}
				</ReactQueryWrapperBase>

				{includesInvestments && (
					<FormField
						label="Composition type"
						error={(valueByPath(formState.errors, "portfolioSavingMode") as { message?: string })?.message}
					>
						{(fieldProps) => (
							<FormController
								control={control}
								name="portfolioSavingMode"
								defaultValue={EditorSaveEditOrReviewRequestPortfolioSavingModeEnum.NestPortfolios}
								render={({ field: { ref: _ref, ...controllerProps } }) => (
									<RadioGroup
										{...controllerProps}
										{...fieldProps}
										value={controllerProps.value ?? null}
										onChange={controllerProps.onChange}
									>
										<div className="flex flex-row flex-wrap gap-4">
											<Radio
												value={EditorSaveEditOrReviewRequestPortfolioSavingModeEnum.NestPortfolios}
												data-qualifier="CompositionEditor/SaveDialog/CompositionMode(Nested)"
											>
												Nested
											</Radio>

											<Radio
												value={EditorSaveEditOrReviewRequestPortfolioSavingModeEnum.MixInstruments}
												data-qualifier="CompositionEditor/SaveDialog/CompositionMode(Aggregate)"
											>
												Aggregate
											</Radio>
										</div>
									</RadioGroup>
								)}
							/>
						)}
					</FormField>
				)}

				<FormField
					label={({ htmlFor }) => (
						<div className="flex items-center w-fit mb-px space-x-2">
							<Label htmlFor={htmlFor}>Weighting method</Label>
							<InfoTooltip align="startToEnd">
								<TooltipContent>
									<Text type="Body/M/Book">
										Drifting weights adjust daily based on market value changes over time, while Fixed weights remain
										unchanged until the next rebalancing.
									</Text>
								</TooltipContent>
							</InfoTooltip>
						</div>
					)}
					error={(valueByPath(formState.errors, "fixedWeights") as { message?: string })?.message}
				>
					{(fieldProps) => (
						<FormController
							control={control}
							name="fixedWeights"
							render={({ field: controllerProps }) => (
								<RadioGroup
									{...controllerProps}
									{...fieldProps}
									onChange={controllerProps.onChange}
									disabled={quantities != null}
								>
									<div className="flex flex-row flex-wrap gap-4">
										<Radio value={false} data-qualifier="CompositionEditor/SaveDialog/weightBehavior(Drifting)">
											Drifting
										</Radio>

										<Radio value data-qualifier="CompositionEditor/SaveDialog/weightBehavior(Fixed)">
											Fixed
										</Radio>
									</div>
								</RadioGroup>
							)}
						/>
					)}
				</FormField>

				<ReactQueryWrapperBase query={customerQuery}>
					{(customer) => (
						<FormField
							label={({ htmlFor }) => (
								<div className="flex items-center gap-2 mb-px">
									<Label htmlFor={htmlFor}>Portfolio type</Label>
									<InfoTooltip align="startToEnd">
										<TooltipContent className="whitespace-pre-line">
											<Text type="Body/M/Bold">Paper Trading</Text>
											<Text type="Body/M/Book">
												{" "}
												keeps the portfolio for internal use only, without enabling execution.{"\n"}
											</Text>
											<Text type="Body/M/Bold">Live Trading</Text>
											<Text type="Body/M/Book">
												{" "}
												makes the portfolio available for execution and allows sending it to external systems for
												trading.
											</Text>
										</TooltipContent>
									</InfoTooltip>
								</div>
							)}
							error={(valueByPath(formState.errors, "portfolioType") as { message?: string })?.message}
						>
							{(fieldProps) => (
								<FormController
									control={control}
									name="liveTrading"
									render={({ field: controllerProps }) => (
										<RadioGroup
											{...controllerProps}
											{...fieldProps}
											value={controllerProps.value ?? null}
											onChange={controllerProps.onChange}
											disabled={!executionAvailable}
										>
											<div className="flex flex-row flex-wrap gap-4">
												<Radio value={false} data-qualifier="portfolioWizard/mainInfo/liveTrading(paper)">
													Paper trading
												</Radio>
												<Radio value data-qualifier="portfolioWizard/mainInfo/liveTrading(live)">
													Live trading
												</Radio>
											</div>
										</RadioGroup>
									)}
								/>
							)}
						</FormField>
					)}
				</ReactQueryWrapperBase>
			</div>
		</Dialog>
	);
}

export type SpawnSubmitManualCreationDialogProps = Omit<SubmitManualCreationDialogProps, "show" | "onClose">;
export function spawnSubmitManualCreationDialog(props: SpawnSubmitManualCreationDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<SubmitManualCreationDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onAsyncSubmit={async (payload) => {
					await props.onAsyncSubmit(payload);
					resolve();
				}}
			/>
		)),
	);
}
